// Holiday detection utilities for Japan

// Japanese national holidays for 2024-2026
// Source: https://www.officeholidays.com/countries/japan
const JAPANESE_HOLIDAYS: Record<string, string> = {
  // 2024
  '2024-01-01': '元日 (New Year\'s Day)',
  '2024-01-08': '成人の日 (Coming of Age Day)',
  '2024-02-11': '建国記念の日 (National Foundation Day)',
  '2024-02-12': '振替休日 (Substitute Holiday)',
  '2024-02-23': '天皇誕生日 (Emperor\'s Birthday)',
  '2024-03-20': '春分の日 (Vernal Equinox Day)',
  '2024-04-29': '昭和の日 (Showa Day)',
  '2024-05-03': '憲法記念日 (Constitution Memorial Day)',
  '2024-05-04': 'みどりの日 (Greenery Day)',
  '2024-05-05': 'こどもの日 (Children\'s Day)',
  '2024-05-06': '振替休日 (Substitute Holiday)',
  '2024-07-15': '海の日 (Marine Day)',
  '2024-08-11': '山の日 (Mountain Day)',
  '2024-08-12': '振替休日 (Substitute Holiday)',
  '2024-09-16': '敬老の日 (Respect for the Aged Day)',
  '2024-09-22': '秋分の日 (Autumnal Equinox Day)',
  '2024-09-23': '振替休日 (Substitute Holiday)',
  '2024-10-14': 'スポーツの日 (Sports Day)',
  '2024-11-03': '文化の日 (Culture Day)',
  '2024-11-04': '振替休日 (Substitute Holiday)',
  '2024-11-23': '勤労感謝の日 (Labor Thanksgiving Day)',

  // 2025
  '2025-01-01': '元日 (New Year\'s Day)',
  '2025-01-13': '成人の日 (Coming of Age Day)',
  '2025-02-11': '建国記念の日 (National Foundation Day)',
  '2025-02-23': '天皇誕生日 (Emperor\'s Birthday)',
  '2025-02-24': '振替休日 (Substitute Holiday)',
  '2025-03-20': '春分の日 (Vernal Equinox Day)',
  '2025-04-29': '昭和の日 (Showa Day)',
  '2025-05-03': '憲法記念日 (Constitution Memorial Day)',
  '2025-05-04': 'みどりの日 (Greenery Day)',
  '2025-05-05': 'こどもの日 (Children\'s Day)',
  '2025-05-06': '振替休日 (Substitute Holiday)',
  '2025-07-21': '海の日 (Marine Day)',
  '2025-08-11': '山の日 (Mountain Day)',
  '2025-09-15': '敬老の日 (Respect for the Aged Day)',
  '2025-09-23': '秋分の日 (Autumnal Equinox Day)',
  '2025-10-13': 'スポーツの日 (Sports Day)',
  '2025-11-03': '文化の日 (Culture Day)',
  '2025-11-23': '勤労感謝の日 (Labor Thanksgiving Day)',
  '2025-11-24': '振替休日 (Substitute Holiday)',

  // 2026
  '2026-01-01': '元日 (New Year\'s Day)',
  '2026-01-12': '成人の日 (Coming of Age Day)',
  '2026-02-11': '建国記念の日 (National Foundation Day)',
  '2026-02-23': '天皇誕生日 (Emperor\'s Birthday)',
  '2026-03-20': '春分の日 (Vernal Equinox Day)',
  '2026-04-29': '昭和の日 (Showa Day)',
  '2026-05-03': '憲法記念日 (Constitution Memorial Day)',
  '2026-05-04': 'みどりの日 (Greenery Day)',
  '2026-05-05': 'こどもの日 (Children\'s Day)',
  '2026-05-06': '振替休日 (Substitute Holiday)',
  '2026-07-20': '海の日 (Marine Day)',
  '2026-08-11': '山の日 (Mountain Day)',
  '2026-09-21': '敬老の日 (Respect for the Aged Day)',
  '2026-09-23': '秋分の日 (Autumnal Equinox Day)',
  '2026-10-12': 'スポーツの日 (Sports Day)',
  '2026-11-03': '文化の日 (Culture Day)',
  '2026-11-23': '勤労感謝の日 (Labor Thanksgiving Day)'
};

/**
 * Check if a date is a weekend (Saturday or Sunday)
 */
export function isWeekend(dateStr: string): boolean {
  const date = new Date(dateStr + 'T00:00:00');
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
}

/**
 * Check if a date is a Japanese holiday
 */
export function isHoliday(dateStr: string): boolean {
  return dateStr in JAPANESE_HOLIDAYS;
}

/**
 * Get holiday name if the date is a holiday
 */
export function getHolidayName(dateStr: string): string | null {
  return JAPANESE_HOLIDAYS[dateStr] || null;
}

/**
 * Check if a date is a non-working day (weekend or holiday)
 */
export function isNonWorkingDay(dateStr: string): boolean {
  return isWeekend(dateStr) || isHoliday(dateStr);
}

/**
 * Get day type for chart overlay
 */
export function getDayType(dateStr: string): 'weekday' | 'weekend' | 'holiday' {
  if (isHoliday(dateStr)) return 'holiday';
  if (isWeekend(dateStr)) return 'weekend';
  return 'weekday';
}

/**
 * Process chart data to add reference areas for weekends and holidays
 */
export function getChartReferenceAreas(data: Array<{ date: string }>): Array<{
  start: string;
  end: string;
  type: 'weekend' | 'holiday';
  name?: string;
}> {
  if (!data || data.length === 0) return [];

  const referenceAreas: Array<{
    start: string;
    end: string;
    type: 'weekend' | 'holiday';
    name?: string;
  }> = [];

  let currentArea: {
    start: string;
    end: string;
    type: 'weekend' | 'holiday';
    name?: string;
  } | null = null;

  data.forEach((item, _index) => {
    const dayType = getDayType(item.date);
    
    if (dayType === 'holiday' || dayType === 'weekend') {
      if (currentArea && currentArea.type === dayType) {
        // Continue the current area
        currentArea.end = item.date;
      } else {
        // End previous area if exists
        if (currentArea) {
          referenceAreas.push(currentArea);
        }
        // Start new area
        currentArea = {
          start: item.date,
          end: item.date,
          type: dayType,
          name: dayType === 'holiday' ? getHolidayName(item.date) || undefined : undefined
        };
      }
    } else {
      // End current area if exists
      if (currentArea) {
        referenceAreas.push(currentArea);
        currentArea = null;
      }
    }
  });

  // Don't forget the last area
  if (currentArea) {
    referenceAreas.push(currentArea);
  }

  return referenceAreas;
}